import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { styled } from 'goober';
import { SearchEntitySelect } from '@/components/search/search-entity-select';
import { isOfType } from '@/helpers/other';
import { APP_USER_ROLES } from '@/types';
import { Stack } from '@/ui/line/line';
import type { CustomAsyncSelectProps } from '@/ui/select/async/use-select-type';

import { SelectedOptionsEntitiesList } from '../selected-options-entities-list';
import type { EntityOption, UserRoleOption } from '../settings.state';
import { settingsChangeUserRoleFormState } from '../settings.state';
import type { SimpleSearchSuggestion } from '@/api/v4/search.api';
import { fetchEntitiesByName } from '@/api/v4/search.api';
import { useSetAtom } from 'jotai';

type ChangeUserRoleProps = {
  userId: number;
  userDisplayName: string;
  currentRole: UserRoleOption;
  newRole: UserRoleOption;
  hasLinkedEntities: boolean;
  setActionDisable: (value: boolean) => void;
};

export const ChangeUserRole = (props: ChangeUserRoleProps) => {
  const { t } = useTranslation(['default', 'settings']);

  const [selectedOptions, setSelectedOptions] = useState<EntityOption[]>([]);
  const setUserRoleForm = useSetAtom(settingsChangeUserRoleFormState);

  useEffect(() => {
    setUserRoleForm({
      userId: props.userId,
      newRoleId: props.newRole.id,
      entities: selectedOptions,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.newRole.id, props.userId, selectedOptions]);

  const setOptions: CustomAsyncSelectProps['setOptions'] = async (
    search,
    page,
  ) => {
    if (search.length < 1) return [];
    const searchResults = await fetchEntitiesByName({
      page,
      search,
      entities: ['organizations', 'projects'],
    });

    const groupedOptions = [
      {
        label: t`default:companies`,
        options: mapToCompanyOptions(
          searchResults.results.filter(s => s.type === 'company'),
        ),
      },
      {
        label: t`default:projects`,
        options: mapToProjectOptions(
          searchResults.results.filter(s => s.type === 'project'),
        ),
      },
    ];

    return groupedOptions;
  };

  const handleOnChange: CustomAsyncSelectProps['onChange'] = (
    newValue,
    actionMeta,
  ) => {
    if (
      actionMeta.action === 'select-option' &&
      isEntityOptionArray(newValue)
    ) {
      setSelectedOptions(newValue);
    }

    if (actionMeta.action === 'remove-value') {
      setSelectedOptions(currentOptions =>
        currentOptions.filter(
          item =>
            isEntityOption(actionMeta.removedValue) &&
            item.value !== actionMeta.removedValue.value,
        ),
      );
    }
  };

  const selectedNewRoleIsGuest = props.newRole.label === APP_USER_ROLES.GUEST;

  return (
    <Content gap="16px">
      <Stack gap="0px">
        <Description>
          <Trans
            i18nKey={`settings:modal.changeRole.warning.${props.currentRole.label.toLowerCase()}.${props.newRole.label.toLowerCase()}${
              props.hasLinkedEntities && selectedNewRoleIsGuest
                ? 'WithLinkedEntities'
                : ''
            }`}
            values={{ name: props.userDisplayName }}
            components={{ bold: <strong /> }}
          />
        </Description>
      </Stack>

      {selectedNewRoleIsGuest && !props.hasLinkedEntities && (
        <SearchEntitySelect
          placeholder={t`settings:searchCompanyOrProject`}
          setOptions={setOptions}
          onChange={handleOnChange}
          defaultValue={selectedOptions}
          value={selectedOptions}
          SelectedOptionsListComponent={<SelectedOptionsEntitiesList />}
        />
      )}
    </Content>
  );
};

const Content = styled(Stack)`
  padding: 24px 0 70px 0;
`;

const Description = styled('p')`
  ${({ theme }) => theme.typography.acrossPlatform.small};
  color: ${({ theme }) => theme.colors.gray.c12};
  width: 640px;

  /* Workaround to make the strong font-weight enabled inside the trans component */
  strong {
    font-weight: 500;
  }
`;

const isEntityOption = (option: unknown): option is EntityOption =>
  isOfType<EntityOption>(option, 'label') &&
  isOfType<EntityOption>(option, 'value') &&
  isOfType<EntityOption>(option, 'type');

const isEntityOptionArray = (option: unknown): option is EntityOption[] =>
  Array.isArray(option) && option.every(isEntityOption);

export const mapToCompanyOptions = (
  organizations: SimpleSearchSuggestion[],
): EntityOption[] =>
  organizations.map(org => ({
    value: org.id,
    label: org.name,
    logo: org.logoUrl,
    domain: org.domain,
    type: 'organization',
  }));

export const mapToProjectOptions = (
  projects: SimpleSearchSuggestion[],
): EntityOption[] =>
  projects.map(org => ({
    value: org.id,
    label: org.name,
    type: 'project',
  }));
